"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const three_1 = require("three");
// eslint-disable-next-line import/no-cycle
const utils_1 = require("../utils");
const Cad3d_scss_1 = __importDefault(require("../Cad3d.scss"));
class WindowSelectionHelper {
    constructor(viewer) {
        this.isMouseDown = false;
        this.isActive = false;
        this.selectedObjects = new Map();
        this.viewer = viewer;
        this.camera = viewer.getCamera();
        this.scene = viewer.getScene();
        this.designProMain = viewer.getDesignProMain();
        this.element = document.createElement('div');
        this.element.classList.add(Cad3d_scss_1.default.selectBox);
        this.element.style.pointerEvents = 'none';
    }
    setStartPoint(clientX, clientY) {
        this.isMouseDown = true;
        this.startPoint = new three_1.Vector2(clientX, clientY);
    }
    setupSelection(event) {
        this.isActive = true;
        this.viewer.getRendererDOMElement().parentElement.appendChild(this.element);
        this.previouslySelected = [];
        if (!event.ctrlKey) {
            this.designProMain.selectionTool.unselectAll();
        }
        else if (this.designProMain.selectionTool.selectedObjects.length) {
            this.previouslySelected = this.designProMain.selectionTool.selectedObjectsUuids;
            this.designProMain.transformControlsInitial.visible = false;
        }
    }
    updateSelection(event) {
        if (!this.isActive) {
            this.setupSelection(event);
        }
        this.isEnclosedOnlyMode = this.startPoint.x < event.clientX;
        this.bottomLeftPoint = new three_1.Vector2(Math.min(this.startPoint.x, event.clientX), Math.min(this.startPoint.y, event.clientY));
        this.topRightPoint = new three_1.Vector2(Math.max(this.startPoint.x, event.clientX), Math.max(this.startPoint.y, event.clientY));
        this.element.style.left = `${this.bottomLeftPoint.x}px`;
        this.element.style.top = `${this.bottomLeftPoint.y}px`;
        this.element.style.width = `${this.topRightPoint.x - this.bottomLeftPoint.x}px`;
        this.element.style.height = `${this.topRightPoint.y - this.bottomLeftPoint.y}px`;
        this.select();
    }
    select() {
        const selectPredicate = this.isEnclosedOnlyMode
            ? this.isEnclosed.bind(this)
            : this.isEnclosedOrIntersected.bind(this);
        this.scene.children.forEach((child) => {
            const baseObject = child;
            if (!baseObject.isCustomObject || this.previouslySelected.includes(baseObject.uuid)) {
                return;
            }
            const shouldSelect = selectPredicate(baseObject.selectionBox);
            if (shouldSelect && !this.selectedObjects.has(baseObject.uuid)) {
                baseObject.select();
                this.selectedObjects.set(baseObject.uuid, baseObject);
            }
            else if (!shouldSelect && this.selectedObjects.has(baseObject.uuid)) {
                baseObject.unselect();
                this.selectedObjects.delete(baseObject.uuid);
            }
        });
    }
    stopSelection(clientX, clientY) {
        this.isEnclosedOnlyMode = this.startPoint.x < clientX;
        this.bottomLeftPoint = new three_1.Vector2(Math.min(this.startPoint.x, clientX), Math.min(this.startPoint.y, clientY));
        this.topRightPoint = new three_1.Vector2(Math.max(this.startPoint.x, clientX), Math.max(this.startPoint.y, clientY));
        this.select();
        this.isActive = false;
        this.element.parentElement.removeChild(this.element);
        if (this.previouslySelected.length) {
            this.designProMain.transformControlsInitial.visible = true;
        }
        if (this.selectedObjects.size > 0) {
            this.selectedObjects.forEach((obj) => obj.unselect());
            this.designProMain.selectionTool.selectObjects([...this.selectedObjects.values()]);
            this.selectedObjects.clear();
        }
    }
    isEnclosed(box) {
        return this.getScreenSpacePoints(box).every((p) => this.isPointInSelectionWindow(p));
    }
    isEnclosedOrIntersected(box) {
        const convexHullPoints = this.getConvexHull(this.getScreenSpacePoints(box));
        // No intersection, object inside window or selection window is inside object.
        if (this.isPointInSelectionWindow(convexHullPoints[0]) ||
            this.isPointInsideConvexHull(this.startPoint, convexHullPoints)) {
            return true;
        }
        const rectanglePoints = [
            this.topRightPoint,
            new three_1.Vector2(this.topRightPoint.x, this.bottomLeftPoint.y),
            this.bottomLeftPoint,
            new three_1.Vector2(this.bottomLeftPoint.x, this.topRightPoint.y),
        ];
        // Check intersections between selection hull and rectangle.
        for (let i = 0; i < convexHullPoints.length; i += 1) {
            const line1A = convexHullPoints[i];
            const line1B = i < convexHullPoints.length - 1 ? convexHullPoints[i + 1] : convexHullPoints[0];
            for (let j = 0; j < rectanglePoints.length; j += 1) {
                const line2A = rectanglePoints[j];
                const line2B = j < rectanglePoints.length - 1 ? rectanglePoints[j + 1] : rectanglePoints[0];
                if (this.doLinesIntersect(line1A, line1B, line2A, line2B)) {
                    return true;
                }
            }
        }
        return false;
    }
    getScreenSpacePoints(box) {
        return box.vertices.map((p) => utils_1.Utils.positionToScreenPosition(p, this.camera));
    }
    isPointInSelectionWindow(point) {
        return (point.x > this.bottomLeftPoint.x &&
            point.x < this.topRightPoint.x &&
            point.y > this.bottomLeftPoint.y &&
            point.y < this.topRightPoint.y);
    }
    // eslint-disable-next-line class-methods-use-this
    isPointInsideConvexHull(point, hull) {
        let isInside = false;
        for (let i = 0, j = hull.length - 1; i < hull.length; j = i, i += 1) {
            const intersect = hull[i].y > point.y !== hull[j].y > point.y &&
                point.x < ((hull[j].x - hull[i].x) * (point.y - hull[i].y)) / (hull[j].y - hull[i].y) + hull[i].x;
            if (intersect) {
                isInside = !isInside;
            }
        }
        return isInside;
    }
    getConvexHull(points) {
        const hullPoints = [];
        let leftmostIndex = 0;
        for (let i = 1; i < points.length; i += 1) {
            if (points[i].x < points[leftmostIndex].x) {
                leftmostIndex = i;
            }
        }
        let currentIndex = leftmostIndex;
        let mostCounterclockwise;
        do {
            hullPoints.push(points[currentIndex]);
            mostCounterclockwise = (currentIndex + 1) % points.length;
            for (let i = 0; i < points.length; i += 1) {
                if (this.isMoreCounterclockwise(points[i], points[currentIndex], points[mostCounterclockwise])) {
                    mostCounterclockwise = i;
                }
            }
            currentIndex = mostCounterclockwise;
        } while (currentIndex !== leftmostIndex);
        return hullPoints;
    }
    // eslint-disable-next-line class-methods-use-this
    isMoreCounterclockwise(check, previous, mostCounterClockwise) {
        const val = (check.y - previous.y) * (mostCounterClockwise.x - check.x) -
            (check.x - previous.x) * (mostCounterClockwise.y - check.y);
        return val < 0;
    }
    // eslint-disable-next-line class-methods-use-this
    doLinesIntersect(line1A, line1B, line2C, line2D) {
        const det = (line1B.x - line1A.x) * (line2D.y - line2C.y) - (line2D.x - line2C.x) * (line1B.y - line1A.y);
        if (det === 0) {
            return false;
        }
        // Check that intersection lies between both sets of points
        const lambda = ((line2D.y - line2C.y) * (line2D.x - line1A.x) + (line2C.x - line2D.x) * (line2D.y - line1A.y)) / det;
        const gamma = ((line1A.y - line1B.y) * (line2D.x - line1A.x) + (line1B.x - line1A.x) * (line2D.y - line1A.y)) / det;
        return lambda > 0 && lambda < 1 && gamma > 0 && gamma < 1;
    }
}
exports.default = WindowSelectionHelper;
